import React, { useState, useEffect } from 'react';
import AppLayout from '../components/layout/AppLayout';
import { useAuth } from '../context/AuthContext';
import { getMyCheckIns, checkIn, checkOut, getAllCheckIns } from '../services/api';
import { Clock, LogIn, LogOut, RefreshCw, MapPin, FileText, Users, CheckCircle, Timer } from 'lucide-react';

/* ── Duration calculator ──────────────────────────────────────── */
function calcDuration(inTime, outTime) {
  if (!inTime) return '—';
  const start = new Date(inTime);
  const end   = outTime ? new Date(outTime) : new Date();
  const mins  = Math.floor((end - start) / 60000);
  const h     = Math.floor(mins / 60);
  const m     = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function fmt(dt, type = 'time') {
  if (!dt) return '—';
  const d = new Date(dt);
  if (type === 'date') return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/* ── Status badge ─────────────────────────────────────────────── */
function StatusBadge({ status }) {
  const isIn = status === 'checked_in';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
      background: isIn ? 'rgba(16,185,129,0.12)' : 'rgba(107,114,128,0.10)',
      color: isIn ? '#059669' : '#6b7280',
      border: `1px solid ${isIn ? 'rgba(16,185,129,0.3)' : 'rgba(107,114,128,0.2)'}`,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: isIn ? '#10b981' : '#9ca3af', flexShrink: 0 }} />
      {isIn ? 'Active' : 'Completed'}
    </span>
  );
}

/* ── Stat card ────────────────────────────────────────────────── */
function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div style={{ flex: 1, minWidth: 120, background: 'var(--bg-body)', borderRadius: 10, padding: '14px 16px', border: '1px solid var(--border-light)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Icon size={14} color={color} />
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}

export default function CheckInOutPage() {
  const { user }  = useAuth();
  const isAdmin   = user?.role === 'admin';

  const [records,    setRecords]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [activeRec,  setActiveRec]  = useState(null);

  const [checkInForm,  setCheckInForm]  = useState({ remarks: '', location: '' });
  const [checkOutForm, setCheckOutForm] = useState({ work_summary: '' });

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res  = isAdmin ? await getAllCheckIns() : await getMyCheckIns();
      const rows = res.data || [];
      setRecords(rows);
      setActiveRec(rows.find(r => r.status === 'checked_in' && r.consultant_id === user?.id) || null);
    } catch {
      setError('Failed to load records.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRecords(); }, []); // eslint-disable-line

  const handleCheckIn = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setSubmitting(true);
    try {
      await checkIn(checkInForm);
      setSuccess('Checked in! SLA clock is now running.');
      setCheckInForm({ remarks: '', location: '' });
      fetchRecords();
    } catch (err) {
      setError(err.response?.data?.error || 'Check-in failed.');
    } finally { setSubmitting(false); }
  };

  const handleCheckOut = async (e) => {
    e.preventDefault();
    if (!activeRec) return;
    setError(''); setSuccess(''); setSubmitting(true);
    try {
      await checkOut(activeRec.id, checkOutForm);
      setSuccess('Checked out. Work session recorded.');
      setCheckOutForm({ work_summary: '' });
      fetchRecords();
    } catch (err) {
      setError(err.response?.data?.error || 'Check-out failed.');
    } finally { setSubmitting(false); }
  };

  /* ── Summary stats ──────────────────────────────────────────── */
  const totalSessions   = records.length;
  const activeSessions  = records.filter(r => r.status === 'checked_in').length;
  const completedSessions = records.filter(r => r.status === 'checked_out').length;

  const totalHours = records
    .filter(r => r.checkout_time)
    .reduce((acc, r) => acc + (new Date(r.checkout_time) - new Date(r.checkin_time)) / 3600000, 0)
    .toFixed(1);

  /* ── Unique consultants (admin) ─────────────────────────────── */
  const uniqueConsultants = isAdmin
    ? [...new Set(records.map(r => r.consultant_id))].length
    : null;

  return (
    <AppLayout pageTitle="Attendance Log">
      {/* ═══ ADMIN: full-width layout ═══════════════════════════ */}
      {isAdmin && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Summary stats */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <StatCard icon={Users}        label="Consultants"   value={uniqueConsultants} color="var(--primary)" />
            <StatCard icon={Clock}        label="Total Sessions" value={totalSessions}     color="#7c3aed" />
            <StatCard icon={CheckCircle}  label="Completed"      value={completedSessions} color="#16a34a" />
            <StatCard icon={LogIn}        label="Active Now"     value={activeSessions}    color="#f59e0b" />
            <StatCard icon={Timer}        label="Total Hours"    value={`${totalHours}h`}  color="#0284c7" />
          </div>

          {/* Table card */}
          <RecordsTable
            records={records}
            loading={loading}
            isAdmin={isAdmin}
            onRefresh={fetchRecords}
          />
        </div>
      )}

      {/* ═══ CONSULTANT: sidebar + table ═══════════════════════ */}
      {!isAdmin && (
        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20, alignItems: 'start' }}>

          {/* Left panel */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Status + form */}
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
              {/* Status indicator */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18,
                padding: '12px 14px', borderRadius: 10,
                background: activeRec ? 'rgba(16,185,129,0.08)' : 'rgba(107,114,128,0.06)',
                border: `1px solid ${activeRec ? 'rgba(16,185,129,0.25)' : 'rgba(107,114,128,0.15)'}`,
              }}>
                <div style={{
                  width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                  background: activeRec ? '#10b981' : '#9ca3af',
                  boxShadow: activeRec ? '0 0 0 4px rgba(16,185,129,0.2)' : 'none',
                }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-main)' }}>
                    {activeRec ? 'Checked In' : 'Checked Out'}
                  </div>
                  {activeRec && (
                    <div style={{ fontSize: 12, color: '#10b981', marginTop: 1 }}>
                      {calcDuration(activeRec.checkin_time, null)} active · SLA running
                    </div>
                  )}
                </div>
              </div>

              {error   && <div style={alertErr}>{error}</div>}
              {success && <div style={alertOk}>{success}</div>}

              {/* Check-in form */}
              {!activeRec && (
                <form onSubmit={handleCheckIn}>
                  <Field label="Location (optional)">
                    <input style={inp} value={checkInForm.location}
                      onChange={e => setCheckInForm(f => ({ ...f, location: e.target.value }))}
                      placeholder="e.g. Office, Remote – Home" />
                  </Field>
                  <Field label="Remarks (optional)">
                    <textarea style={{ ...inp, resize: 'vertical' }} rows={3}
                      value={checkInForm.remarks}
                      onChange={e => setCheckInForm(f => ({ ...f, remarks: e.target.value }))}
                      placeholder="Today's work plan…" />
                  </Field>
                  <button type="submit" disabled={submitting} style={{ ...actionBtn, background: submitting ? '#94a3b8' : '#10b981' }}>
                    <LogIn size={15} /> {submitting ? 'Checking in…' : 'Check In'}
                  </button>
                </form>
              )}

              {/* Check-out form */}
              {activeRec && (
                <form onSubmit={handleCheckOut}>
                  <div style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 14, background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.2)', fontSize: 12 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-main)', marginBottom: 4 }}>Current Session</div>
                    <div style={{ color: 'var(--text-muted)' }}>Started: <b style={{ color: 'var(--text-main)' }}>{fmt(activeRec.checkin_time)}</b></div>
                    <div style={{ color: 'var(--text-muted)' }}>Duration: <b style={{ color: '#10b981' }}>{calcDuration(activeRec.checkin_time, null)}</b></div>
                    {activeRec.location && <div style={{ color: 'var(--text-muted)' }}>Location: {activeRec.location}</div>}
                  </div>
                  <Field label="Work Summary *">
                    <textarea style={{ ...inp, resize: 'vertical' }} rows={4} required
                      value={checkOutForm.work_summary}
                      onChange={e => setCheckOutForm(f => ({ ...f, work_summary: e.target.value }))}
                      placeholder="Summarize what you worked on…" />
                  </Field>
                  <button type="submit" disabled={submitting} style={{ ...actionBtn, background: submitting ? '#94a3b8' : '#ef4444' }}>
                    <LogOut size={15} /> {submitting ? 'Checking out…' : 'Check Out'}
                  </button>
                </form>
              )}
            </div>

            {/* SLA rules card */}
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>
                SLA Clock Rules
              </div>
              {[
                { icon: '✅', text: 'SLA runs only during checked-in hours' },
                { icon: '⏸️', text: 'SLA pauses on check-out' },
                { icon: '📅', text: 'SLA pauses on holidays & off-hours' },
                { icon: '🗓️', text: 'SLA pauses on approved leaves' },
                { icon: '📊', text: 'All hours tracked for productivity' },
              ].map(item => (
                <div key={item.text} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 7, fontSize: 12, color: 'var(--text-sub)' }}>
                  <span style={{ flexShrink: 0, lineHeight: '16px' }}>{item.icon}</span>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right panel — table */}
          <RecordsTable
            records={records}
            loading={loading}
            isAdmin={false}
            onRefresh={fetchRecords}
          />
        </div>
      )}
    </AppLayout>
  );
}

/* ── Records table component ──────────────────────────────────── */
function RecordsTable({ records, loading, isAdmin, onRefresh }) {
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-main)' }}>
            {isAdmin ? 'All Consultant Check-In Records' : 'My Check-In History'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {records.length} session{records.length !== 1 ? 's' : ''} recorded
          </div>
        </div>
        <button
          onClick={onRefresh}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-body)', color: 'var(--text-muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Body */}
      {loading ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
          <RefreshCw size={22} style={{ animation: 'spin 1s linear infinite', marginBottom: 12 }} />
          <div style={{ fontSize: 13 }}>Loading records…</div>
        </div>
      ) : records.length === 0 ? (
        <div style={{ padding: 56, textAlign: 'center' }}>
          <Clock size={36} color="var(--text-muted)" style={{ marginBottom: 12, opacity: 0.4 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-main)', marginBottom: 4 }}>No check-in records yet</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Use the Check In button to start your work session.</div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isAdmin ? 760 : 560 }}>
            <thead>
              <tr style={{ background: 'var(--bg-body)' }}>
                {isAdmin && <Th>Consultant</Th>}
                <Th>Date</Th>
                <Th>Check In</Th>
                <Th>Check Out</Th>
                <Th>Duration</Th>
                <Th>Status</Th>
                <Th>Location</Th>
                <Th>Work Summary</Th>
              </tr>
            </thead>
            <tbody>
              {records.map((rec, i) => (
                <tr key={rec.id}
                  style={{ borderBottom: '1px solid var(--border-light)', background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.012)', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-hover-row, rgba(0,0,0,0.03))'}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.012)'}
                >
                  {isAdmin && (
                    <td style={td}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-main)', whiteSpace: 'nowrap' }}>{rec.consultant_name || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{rec.consultant_email}</div>
                    </td>
                  )}
                  <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 13 }}>{fmt(rec.checkin_time, 'date')}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                    {fmt(rec.checkin_time)}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontSize: 13, color: rec.checkout_time ? 'var(--text-main)' : '#f59e0b' }}>
                    {rec.checkout_time ? fmt(rec.checkout_time) : 'Active'}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 700, fontSize: 13, color: rec.status === 'checked_in' ? '#10b981' : 'var(--text-main)' }}>
                    {calcDuration(rec.checkin_time, rec.checkout_time)}
                  </td>
                  <td style={td}><StatusBadge status={rec.status} /></td>
                  <td style={{ ...td, maxWidth: 130 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-muted)' }}>
                      {rec.location ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <MapPin size={11} /> {rec.location}
                        </span>
                      ) : '—'}
                    </div>
                  </td>
                  <td style={{ ...td, maxWidth: 220 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-muted)' }}
                      title={rec.work_summary || rec.remarks || ''}>
                      {rec.work_summary || rec.remarks ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <FileText size={11} /> {rec.work_summary || rec.remarks}
                        </span>
                      ) : '—'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }) {
  return (
    <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' }}>
      {children}
    </th>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 13 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inp = {
  width: '100%', padding: '8px 11px',
  border: '1px solid var(--border)', borderRadius: 8,
  background: 'var(--bg-body)', color: 'var(--text-main)',
  fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

const actionBtn = {
  width: '100%', padding: '10px', borderRadius: 9, border: 'none',
  color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  transition: 'opacity 0.15s',
};

const alertErr = {
  padding: '9px 12px', borderRadius: 8, marginBottom: 12,
  background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
  color: '#dc2626', fontSize: 12,
};

const alertOk = {
  padding: '9px 12px', borderRadius: 8, marginBottom: 12,
  background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)',
  color: '#059669', fontSize: 12,
};

const td = { padding: '11px 14px', verticalAlign: 'middle' };
